/** Phase C: current Payout Run vs the routed view, per period, with the root-liability gate. */
import React from 'react';
import { StatusPill, money } from '../finance/financeUi';

const TONE = { MATCH: 'text-emerald-700', EXPECTED_ROUTING_DIFFERENCE: 'text-sky-700', DATA_QUALITY: 'text-amber-700', ROUTING_BUG: 'text-red-700', REQUIRES_REVIEW: 'text-orange-700' };

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border px-3 py-2"><p className="text-xs text-muted-foreground">{label}</p><p className="text-lg font-semibold tabular-nums" data-testid={testid}>{value}</p></div>
);

function Edges({ title, rows, testid }) {
  if (!rows.length) return null;
  return (
    <div data-testid={testid}>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <ul className="divide-y rounded-lg border text-sm">
        {rows.map((e, i) => <li key={i} className="flex justify-between px-3 py-1.5"><span>{e.payer ? `${e.payer} → ` : 'Root → '}{e.payee}</span><span className="tabular-nums">{money(e.amount)}</span></li>)}
      </ul>
    </div>
  );
}

export function ComparisonResult({ c }) {
  return (
    <div className="space-y-3 rounded-xl border p-4" data-testid={`comparison-${c.period}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{c.period} <span className="text-xs text-muted-foreground">· {c.mode === 'what_if' ? 'What-if (includes proposals)' : 'Effective records'} · current = {c.current_source}</span></p>
        <StatusPill value={c.gate} testid={`comparison-gate-${c.period}`} />
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <Stat label="Current root liability" value={money(c.current_root_liability)} testid={`current-root-${c.period}`} />
        <Stat label="Routed root liability" value={money(c.routed_root_liability)} testid={`routed-root-${c.period}`} />
        <Stat label="Difference" value={money(c.difference)} testid={`difference-${c.period}`} />
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`counts-${c.period}`}>{Object.entries(c.counts).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')} — {c.live_effect}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Edges title="Root pays" rows={c.root_edges} testid={`root-edges-${c.period}`} />
        <Edges title="Downstream obligations (not root liabilities)" rows={c.downstream_edges} testid={`downstream-edges-${c.period}`} />
      </div>
      <ul className="divide-y rounded-lg border text-sm" data-testid={`payees-${c.period}`}>
        {(c.payees || []).map((p) => (
          <li key={p.payee_user_id} className="flex flex-wrap justify-between gap-2 px-3 py-1.5">
            <span>{p.payee_name}</span>
            <span className="tabular-nums text-xs">earned {money(p.step9_earned)} · current {money(p.current_amount)} · routed root {money(p.routed_root_payable)}</span>
            <span className={`text-xs font-medium ${TONE[p.classification]}`} data-testid={`class-${c.period}-${p.payee_user_id}`}>{p.classification.replace(/_/g, ' ')}{p.issues.length ? ` (${p.issues.join(', ')})` : ''}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
